import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { cn } from '@/shared/lib/cn';
import type { WebsiteAnalyticsRecentEvent } from '../types';
import { isKnownWebsiteEvent, websiteEventLabelKey } from '../utils/eventLabels';

interface WebsiteActivityFeedProps {
  events: WebsiteAnalyticsRecentEvent[];
  locale: string;
}

export function WebsiteActivityFeed({ events, locale }: WebsiteActivityFeedProps) {
  const { t } = useTranslation();

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-base">{t('websiteActivity.feed.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-border">
          {events.map((event) => (
            <li
              key={event.id}
              className="flex flex-col gap-1 px-6 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-medium text-foreground">
                  {isKnownWebsiteEvent(event.eventType)
                    ? t(websiteEventLabelKey(event.eventType))
                    : t('websiteActivity.events.unknown', { type: event.eventType })}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {event.productSlug}
                  {event.planId ? (
                    <span className="font-mono">
                      {' '}
                      · {t('websiteActivity.feed.plan')}: {event.planId}
                    </span>
                  ) : null}
                </p>
              </div>
              <time
                dateTime={event.createdAt}
                className={cn('shrink-0 text-xs tabular-nums text-muted-foreground', 'sm:text-end')}
              >
                {new Date(event.createdAt).toLocaleString(locale)}
              </time>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
